"use client";

import { ArrowRight, ChevronRight, Layers, SlidersHorizontal, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
export default function ProductsHero() {
  const {
    lang
  } = useLanguage();
  const technicalSpecs = [{
    label: pick(lang, {
      en: "Standards",
      zh: "执行标准",
      es: "Normas",
      de: "Normen",
      fr: "Normes",
      pt: "Normas"
    }),
    val: "ISO 9044 / ASTM E2016"
  }, {
    label: pick(lang, {
      en: "Category",
      zh: "标准编织",
      es: "Categoría",
      de: "Kategorie",
      fr: "Catégorie",
      pt: "Categoria"
    }),
    val: pick(lang, {
      en: "Woven Wire Mesh",
      zh: "编织丝网",
      es: "Malla de alambre tejida",
      de: "Drahtgewebe",
      fr: "Toile métallique tissée",
      pt: "Malha de arame tecida"
    })
  }, {
    label: pick(lang, {
      en: "Category",
      zh: "多层烧结",
      es: "Categoría",
      de: "Kategorie",
      fr: "Catégorie",
      pt: "Categoria"
    }),
    val: pick(lang, {
      en: "Sintered Wire Mesh",
      zh: "烧结丝网",
      es: "Malla de alambre sinterizada",
      de: "Sinterdrahtgewebe",
      fr: "Toile métallique frittée",
      pt: "Malha de arame sinterizada"
    })
  }, {
    label: pick(lang, {
      en: "Categories",
      zh: "深加工与筛分",
      es: "Categorías",
      de: "Kategorien",
      fr: "Catégories",
      pt: "Categorias"
    }),
    val: pick(lang, {
      en: "Filter Components & Wedge Wire",
      zh: "过滤组件 / 楔形丝筛",
      es: "Componentes de filtración y alambre en cuña",
      de: "Filterkomponenten & Spaltsiebe",
      fr: "Composants de filtration et fils profilés",
      pt: "Componentes de filtração e arame em cunha"
    })
  }];
  return <section id="productshero" data-nav="light" className="relative w-full border-b border-border bg-background mf-hero">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Left Column: Text & Actions */}
          <div className="lg:col-span-7 flex flex-col justify-center items-start">
            <Reveal>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono tracking-tight text-foreground mf-caption">
                  <Layers className="size-3.5 text-primary" />
                  {pick(lang, {
                  en: "Industrial Filtration Products",
                  zh: "工业过滤与筛分产品系列",
                  es: "Productos de filtración industrial",
                  de: "Industrielle Filtrationsprodukte",
                  fr: "Produits de filtration industrielle",
                  pt: "Produtos de filtração industrial"
                })}
                </Badge>
                <Badge variant="outline" className="bg-muted text-foreground rounded-md border border-border px-2.5 py-1 font-mono mf-caption">
                  <ShieldCheck className="mr-1 size-3 text-primary inline" />
                  ISO 9044 / ASTM E2016
                </Badge>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <h1 className="font-default mf-h1 text-foreground">
                {pick(lang, {
                en: "Industrial Filtration Products",
                zh: "工业过滤与分离产品系列",
                es: "Productos de filtración industrial",
                de: "Industrielle Filtrationsprodukte",
                fr: "Produits de filtration industrielle",
                pt: "Produtos de filtração industrial"
              })}
              </h1>
            </Reveal>

            <Reveal delay={160}>
              <p className="mt-4 max-w-2xl text-muted-foreground mf-lead font-normal">
                {pick(lang, {
                en: "MutualFilter supplies woven wire mesh, sintered wire mesh, filter components, and wedge wire screens for industrial filtration, screening, and separation.",
                zh: "MutualFilter 提供编织丝网、烧结丝网、过滤组件及楔形丝筛，满足工业过滤、筛分与固液分离需求。",
                es: "MutualFilter suministra malla de alambre tejida, malla sinterizada, componentes de filtración y tamices de alambre en cuña para filtración, cribado y separación industrial.",
                de: "MutualFilter liefert Drahtgewebe, Sinterdrahtgewebe, Filterkomponenten und Spaltsiebe für industrielle Filtration, Siebung und Trennung.",
                fr: "MutualFilter fournit des toiles métalliques tissées, toiles frittées, composants filtrants et crépines à fil profilé pour la filtration, le criblage et la séparation.",
                pt: "A MutualFilter fornece malha de arame tecida, malha sinterizada, componentes de filtração e telas de arame em cunha para filtração, peneiramento e separação."
              })}
              </p>
            </Reveal>

            <Reveal delay={220}>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 w-full sm:w-auto">
                <Button asChild size="lg" className="px-6 font-semibold">
                  <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                    <span>{pick(lang, COMMON.requestQuote)}</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="px-6 font-semibold bg-card">
                  <Link to="/ApplicationsPage" className="inline-flex items-center gap-2">
                    <span>{pick(lang, COMMON.exploreApplications)}</span>
                    <ChevronRight className="size-4 text-muted-foreground" />
                  </Link>
                </Button>
              </div>
            </Reveal>
          </div>

          {/* Right Column: Visual Card with Image & Specs */}
          <div className="lg:col-span-5 flex flex-col justify-center">
            <Reveal delay={100}>
              <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
                <div className="mf-card-media mf-ratio-hero w-full relative">
                  <img alt={pick(lang, {
                  en: "Precision stainless steel woven wire mesh, filter discs, and industrial screen components",
                  zh: "精密不锈钢编织网、滤片组件及工业筛网产品",
                  es: "Malla de alambre tejida de acero inoxidable de precisión, discos filtrantes y componentes de tamices industriales",
                  de: "Präzisions-Drahtgewebe aus Edelstahl, Filterscheiben und industrielle Siebkomponenten",
                  fr: "Toile métallique tissée en acier inoxydable de précision, disques filtrants et composants de tamis industriels",
                  pt: "Malha de arame tecida de aço inoxidável de precisão, discos de filtro e componentes de telas industriais"
                })} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" src="https://mutualfilter.com/wp-content/uploads/2026/10/cnc-weaving-mesh-1.jpg" />
                  <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md font-mono text-foreground flex items-center gap-1.5 mf-caption">
                    <SlidersHorizontal className="size-3.5 text-primary" />
                    <span>
                      {pick(lang, {
                      en: "Stainless steel",
                      zh: "不锈钢",
                      es: "Acero inoxidable",
                      de: "Edelstahl",
                      fr: "Acier inoxydable",
                      pt: "Aço inoxidável"
                    })}
                    </span>
                  </div>
                </div>

                <CardContent className="p-4">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <CheckCircle2 className="size-3.5 text-primary" />
                      <span>
                        {pick(lang, {
                        en: "Rolls, slit coils, cylinders & mesh panels",
                        zh: "原卷、分条带、滤筒及定制网片",
                        es: "Rollos, bobinas cortadas, cilindros y paneles de malla",
                        de: "Rollen, Spaltbänder, Zylinder & Maschenpaneele",
                        fr: "Rouleaux, bobines refendues, cylindres et panneaux de toile",
                        pt: "Rolos, bobinas cortadas, cilindros e painéis de malha"
                      })}
                      </span>
                    </div>
                    <Link to="/ProductCatalogPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                      <span>
                        {pick(lang, {
                        en: "Catalog",
                        zh: "产品目录",
                        es: "Catálogo",
                        de: "Katalog",
                        fr: "Catalogue",
                        pt: "Catálogo"
                      })}
                      </span>
                      <ArrowRight className="size-3" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>

        {/* Spec Strip below 12-col grid */}
        <div className="mf-spec-strip">
          {technicalSpecs.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value">{spec.val}</span>
            </div>)}
        </div>
      </div>
    </section>;
}